import { marketingSource, blogSource, featureSource, audienceSource } from "./content-sources";
import type { MarketingMetadata } from "./marketing-schema";
import { breadcrumbSchema, faqSchema, organizationSchema, websiteSchema } from "@/lib/seo";

export const marketingSources = {
  compare: marketingSource,
  blog: blogSource,
  feature: featureSource,
  audience: audienceSource,
};
export type MarketingFamily = keyof typeof marketingSources;
export const collectionLabels = {
  compare: "Comparisons",
  blog: "Blog",
  feature: "Features",
  audience: "Audience",
};

export function articleCards(family: MarketingFamily) {
  return marketingSources[family]
    .getPages()
    .filter((page) => page.url !== `/${family}`)
    .sort(
      (a, b) =>
        (a.data.order ?? Infinity) - (b.data.order ?? Infinity) ||
        (family === "blog"
          ? (b.data.datePublished ?? "").localeCompare(a.data.datePublished ?? "")
          : 0) ||
        a.url.localeCompare(b.url),
    )
    .map((page) => ({
      url: page.url,
      title: page.data.cardTitle ?? page.data.title,
      description: page.data.cardDescription ?? page.data.description ?? "",
      datePublished: page.data.datePublished,
      dateModified: page.data.dateModified,
    }));
}
export type ArticleCard = ReturnType<typeof articleCards>[number];

export function surfaceMetadata(family: MarketingFamily, url: string, data: MarketingMetadata) {
  const isHub = url === `/${family}`;
  return {
    eyebrow: data.eyebrow ?? collectionLabels[family],
    lastUpdated: data.lastUpdated,
    breadcrumbs: data.breadcrumbs ?? [
      { label: collectionLabels[family], ...(isHub ? {} : { href: `/${family}` }) },
      ...(isHub ? [] : [{ label: data.title }]),
    ],
    ctas: data.ctas ?? [
      { label: "Start building for free", href: "/login" },
      { label: "Read the documentation", href: "/docs" },
    ],
  };
}

export function marketingSchemas(family: MarketingFamily, url: string, data: MarketingMetadata) {
  if (data.schemas) return data.schemas;
  const crumbs = surfaceMetadata(family, url, data).breadcrumbs;
  return [
    organizationSchema(),
    websiteSchema(),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      ...crumbs.map((crumb) => ({ name: crumb.label, path: crumb.href ?? url })),
    ]),
    ...(data.faqs?.length ? [faqSchema(data.faqs)] : []),
  ];
}
